/**
 * src/lib/loreGenerator.ts
 * Generates dynamic character profiles, D&D 5e archetypes, and quest hooks
 * for any name in the generator.
 */

export interface LoreProfile {
  name: string;
  meaning: string;
  archetype: string;
  alignment: string;
  dndClass: string;
  questHook: string;
  signatureRelic: string;
  personalityTrait: string;
}

const ALIGNMENTS = [
  "Lawful Good", "Neutral Good", "Chaotic Good",
  "Lawful Neutral", "True Neutral", "Chaotic Neutral",
  "Lawful Evil", "Neutral Evil", "Chaotic Evil"
];

const DND_CLASSES = [
  "Paladin (Oath of Devotion)", "Rogue (Arcane Trickster)", "Wizard (School of Evocation)",
  "Sorcerer (Draconic Bloodline)", "Warlock (The Archfey)", "Cleric (Light Domain)",
  "Bard (College of Lore)", "Ranger (Gloom Stalker)", "Druid (Circle of the Moon)",
  "Fighter (Battle Master)", "Monk (Way of the Shadow)", "Artificer (Alchemist)"
];

const ARCHETYPES = [
  "Exiled Sovereign", "Starlight Seeker", "Cursed Spellblade",
  "Ancient Cartographer", "Shadow Guild Infiltrator", "Planar Warden",
  "Runic Blacksmith", "Oracle of the Primordial Tide", "High Arbiter of Oaths",
  "Wandering Fjord Skald", "Celestial Vanguard", "Alchemical Transmuter"
];

const QUEST_HOOKS = [
  "Inherited an unbroken obsidian seal that whispers forgotten incantations when exposed to starlight.",
  "Seeking redemption after unintentionally unlocking a sealed subterranean planar rift beneath kingdom walls.",
  "Pursued by royal inquisitors for carrying the final surviving blueprint of an ancient celestial engine.",
  "Contracted by the Merchant Guild to recover a lost airship wreckage deep within enchanted dragon territory.",
  "Carries a dormant soul-brand that illuminates whenever an ancient elder wyrm awakens nearby.",
  "Sworn to deliver an ancient treaty to the deep sylvan court before the winter solstice eclipse begins."
];

const SIGNATURE_RELICS = [
  "Gilded Astrolabe of the Void", "Whispering Runebound Dagger", "Emberstone Amulet",
  "Tears of the Silver Wyrm", "Grimoire of Frozen Eclipses", "Lantern of Perpetual Dawn",
  "Prismatic Glass Compass", "Ancestral Iron Vanguard Ring", "Vial of Astral Quicksilver"
];

const TRAITS = [
  "Never breaks a sworn word, regardless of personal cost or mortal danger.",
  "Speaks in quiet, measured riddles when discussing matters of planar balance.",
  "Collects rare pressed wildflowers and lunar petals from every realm visited.",
  "Carries a deep instinctual suspicion of illusion magic and gilded promises.",
  "Finds solace in stormy sea shores and roaring forge hearths."
];

function seededHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

export function generateLoreProfile(name: string, meaning?: string, category?: string): LoreProfile {
  const hash = seededHash(name + (category || ""));

  return {
    name,
    meaning: meaning || "A storied title carrying ancient cultural resonance and ancestral distinction.",
    archetype: ARCHETYPES[hash % ARCHETYPES.length],
    alignment: ALIGNMENTS[(hash * 3) % ALIGNMENTS.length],
    dndClass: DND_CLASSES[(hash * 7) % DND_CLASSES.length],
    questHook: QUEST_HOOKS[(hash * 11) % QUEST_HOOKS.length],
    signatureRelic: SIGNATURE_RELICS[(hash * 13) % SIGNATURE_RELICS.length],
    personalityTrait: TRAITS[(hash * 17) % TRAITS.length],
  };
}
